import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Users, CalendarDays, Clock, PieChart, ChevronRight, Filter } from 'lucide-react';
import { User, OOORequest } from '../types';
import { Badge, Modal, ProgressBar } from './ui';
import { BalanceEditor } from './BalanceEditor';
interface AdminDashboardProps {
  allUsers: User[];
  allRequests: OOORequest[];
  onUpdateBalance?: (userId: string, ptoTotal: number, ptoUsed: number) => void;
}
export function AdminDashboard({
  allUsers,
  allRequests,
  onUpdateBalance
}: AdminDashboardProps) {
  const [roleFilter, setRoleFilter] = useState<'All' | 'Processor' | 'Loan Officer'>('All');
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  // Filter users to only requesters (Processors and Loan Officers)
  const requesters = useMemo(() => {
    return allUsers.filter(u => u.role === 'Processor' || u.role === 'Loan Officer');
  }, [allUsers]);
  // Org-wide stats
  const stats = useMemo(() => {
    const totalAllotted = requesters.reduce((sum, u) => sum + (u.ptoTotal || 0), 0);
    const totalUsed = requesters.reduce((sum, u) => sum + (u.ptoUsed || 0), 0);
    const pendingRequests = allRequests.filter(r => r.status === 'Pending').length;
    return {
      totalPeople: requesters.length,
      used: totalUsed,
      remaining: totalAllotted - totalUsed,
      pending: pendingRequests
    };
  }, [requesters, allRequests]);
  // Filtered list for the table
  const filteredUsers = useMemo(() => {
    if (roleFilter === 'All') return requesters;
    return requesters.filter(u => u.role === roleFilter);
  }, [requesters, roleFilter]);
  // Selected member's requests
  const selectedMemberRequests = useMemo(() => {
    if (!selectedMember) return [];
    return allRequests.filter(r => r.userId === selectedMember.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allRequests, selectedMember]);
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6" data-id="element-96">
      <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-97">
        Org-Wide PTO Overview
      </h2>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-98">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-99">
          <div className="p-3 bg-blue-50 text-[#0176d3] rounded-full" data-id="element-100">
            <Users className="w-6 h-6" data-id="element-101" />
          </div>
          <div data-id="element-102">
            <p className="text-sm font-medium text-gray-500" data-id="element-103">Total Personnel</p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-104">
              {stats.totalPeople}
            </p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-105">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-full" data-id="element-106">
            <CalendarDays className="w-6 h-6" data-id="element-107" />
          </div>
          <div data-id="element-108">
            <p className="text-sm font-medium text-gray-500" data-id="element-109">Org PTO Used</p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-110">
              {stats.used}{' '}
              <span className="text-sm font-normal text-gray-500" data-id="element-111">days</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-112">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-113">
            <PieChart className="w-6 h-6" data-id="element-114" />
          </div>
          <div data-id="element-115">
            <p className="text-sm font-medium text-gray-500" data-id="element-116">
              Org PTO Remaining
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-117">
              {stats.remaining}{' '}
              <span className="text-sm font-normal text-gray-500" data-id="element-118">days</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-119">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-full" data-id="element-120">
            <Clock className="w-6 h-6" data-id="element-121" />
          </div>
          <div data-id="element-122">
            <p className="text-sm font-medium text-gray-500" data-id="element-123">
              Pending Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-124">{stats.pending}</p>
          </div>
        </div>
      </div>

      {/* Unified Roster */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-125">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-126">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-127">
            Personnel Balances
          </h3>

          <div className="flex items-center space-x-2" data-id="element-128">
            <Filter className="w-4 h-4 text-gray-500" data-id="element-129" />
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value as any)} className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-[#0176d3] focus:border-[#0176d3] py-1.5 pl-3 pr-8" data-id="element-130">
              <option value="All" data-id="element-131">All Roles</option>
              <option value="Processor" data-id="element-132">Processors</option>
              <option value="Loan Officer" data-id="element-133">Loan Officers</option>
            </select>
          </div>
        </div>

        {filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-134">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-135" />
            <p className="text-gray-500" data-id="element-136">
              No personnel found matching the filter.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-137">
            {filteredUsers.map(user => {
          const userRequestsCount = allRequests.filter(r => r.userId === user.id).length;
          const pendingCount = allRequests.filter(r => r.userId === user.id && r.status === 'Pending').length;
          return <li key={user.id} data-id="element-138">
                  <button type="button" onClick={() => setSelectedMember(user)} aria-label={`View details for ${user.name}`} className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-6 focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-[#0176d3]" data-id="element-139">
                    <div className="md:w-1/3 flex flex-col items-start" data-id="element-140">
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-141">
                        {user.name}
                      </h4>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1" data-id="element-142">
                        {user.role}
                      </span>
                    </div>

                    <div className="md:w-1/6 text-sm text-gray-500" data-id="element-143">
                      <div className="font-medium text-gray-900" data-id="element-144">
                        {userRequestsCount} total requests
                      </div>
                      {pendingCount > 0 && <div className="text-amber-600" data-id="element-145">
                          {pendingCount} pending
                        </div>}
                    </div>

                    <div className="md:w-1/2 max-w-md w-full flex items-center gap-4" data-id="element-146">
                      <div className="flex-1" data-id="element-147">
                        {typeof user.ptoTotal === 'number' ? <ProgressBar used={user.ptoUsed || 0} total={user.ptoTotal} data-id="element-148" /> : <span className="text-sm text-gray-400 italic" data-id="element-149">
                            No PTO balance
                          </span>}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-150" />
                    </div>
                  </button>
                </li>;
        })}
          </ul>}
      </div>

      {/* Member Detail Modal (reused pattern) */}
      <Modal isOpen={selectedMember !== null} onClose={() => setSelectedMember(null)} title={selectedMember ? `${selectedMember.name} — PTO Details` : 'PTO Details'} maxWidth="max-w-2xl" data-id="element-151">
        {selectedMember && <div className="space-y-4" data-id="element-152">
            <BalanceEditor user={allUsers.find(u => u.id === selectedMember.id) || selectedMember} onUpdateBalance={onUpdateBalance} data-id="element-153" />

            {selectedMemberRequests.length === 0 ? <div className="p-10 text-center" data-id="element-154">
                <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-155" />
                <p className="text-gray-500" data-id="element-156">No past requests found.</p>
              </div> : <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden" data-id="element-157">
                {selectedMemberRequests.map(request => <li key={request.id} className="p-4 space-y-3" data-id="element-158">
                    <Badge status={request.status} data-id="element-159" />
                    <div className="flex items-center text-sm text-gray-600" data-id="element-160">
                      <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-161">
                        <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-162" />
                        <span className="font-medium" data-id="element-163">
                          {formatDate(request.startDate)}
                        </span>
                        <span className="mx-2 text-gray-400" data-id="element-164">→</span>
                        <span className="font-medium" data-id="element-165">
                          {formatDate(request.endDate)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-166">
                      {request.message}
                    </div>
                    {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600" data-id="element-167">
                          <span className="font-medium mr-2" data-id="element-168">
                            Coverage assigned:
                          </span>
                          {request.coverageIds.map(id => allUsers.find(u => u.id === id)?.name).join(', ')}
                        </div>}
                  </li>)}
              </ul>}
          </div>}
      </Modal>
    </motion.div>;
}